import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import Timer from '../../../components/elements/Timer';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useSelector';
import {
  getClassicDuel,
  getDuel,
  getQuickDuel,
  getTeamDuel,
} from '../../../store/selectors/settings.selector';
import { getActivePlayer, getInGame, getTeam } from '../../../store/selectors/team.selector';
import { settingsActions } from '../../../store/slices/settings.slice';
import { teamActions } from '../../../store/slices/team.slice';

import { HiddenAudio } from './styles';

interface IProps {
  player: 'player1' | 'player2';
  subPlayer: 'player1' | 'player2';
  defaultTitle: string;
  defaultColor: 'red' | 'blue';
  isMainClock?: boolean;
  onClickPause: () => void;
}

const TimerContainer: FC<IProps> = ({
  player,
  defaultTitle,
  defaultColor,
  subPlayer,
  isMainClock = false,
  onClickPause,
}) => {
  const dispatch = useAppDispatch();
  const [params] = useSearchParams();
  const classicSettings = useAppSelector(getClassicDuel);
  const quickSettings = useAppSelector(getQuickDuel);
  const teamSettings = useAppSelector(getTeamDuel);
  const [duration, setDuration] = useState(
    params.get('game_mode') === 'classic game'
      ? classicSettings.classicDuel.roundTime.min * 60 + classicSettings.classicDuel.roundTime.sec
      : params.get('game_mode') === 'quick game'
        ? quickSettings.roundTime.min * 60 + quickSettings.roundTime.sec
        : teamSettings.teamDuel.roundTime.min * 60 + teamSettings.teamDuel.roundTime.sec,
  );
  const { [player]: gamer, [subPlayer]: ungamer } = useAppSelector(getTeam);
  const { [player]: playerSettings, isTimeSelected } = useAppSelector(getDuel);
  // const [isBlinking, setIsBlinking] = useState(false);
  const activePlayer = useAppSelector(getActivePlayer);
  const inGame = useAppSelector(getInGame);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  const onChangeRemainingTime = useCallback(
    (remainingTime: number) => {
      const min = Math.floor(remainingTime / 60);
      const sec = remainingTime - min * 60;

      dispatch(teamActions.setPlayerRemainingTime({ player, remainingTime }));
      dispatch(settingsActions.setPartialDuel({ player, settings: { min, sec } }));
    },
    [player, dispatch],
  );

  const onClickProtest = useCallback(() => {
    dispatch(teamActions.protest());
  }, [dispatch]);

  const onClickContinue = useCallback(() => {
    dispatch(teamActions.setPlaying({ player }));
  }, [dispatch, player]);

  const onComplete = useCallback(
    (totalElapsedTime: number) => {
      if (
        ((params.get('game_mode') === 'classic game' &&
          classicSettings.classicDuel.isEnabledEndOfRoundSound) ||
          (params.get('game_mode') === 'quick game' && quickSettings.isEnabledEndOfRoundSound) ||
          (params.get('game_mode') === 'team game' &&
            teamSettings.teamDuel.isEnabledEndOfRoundSound)) &&
        audioRef.current
      ) {
        audioRef.current.play();
      }

      dispatch(teamActions.onCompleteGame({ player }));
      return { shouldRepeat: true, newInitialRemainingTime: totalElapsedTime };
    },
    [
      params,
      classicSettings.classicDuel.isEnabledEndOfRoundSound,
      quickSettings.isEnabledEndOfRoundSound,
      teamSettings.teamDuel.isEnabledEndOfRoundSound,
      dispatch,
      player,
      gamer.remainingTime,
      ungamer.remainingTime,
    ],
  );

  const setName = useCallback(
    (name: string) => {
      dispatch(teamActions.setName({ name, player }));
      dispatch(teamActions.setColor({ player, color: gamer.teamColor || defaultColor }));
    },
    [dispatch, player, gamer.teamColor, defaultColor],
  );

  useEffect(() => {
    if (playerSettings.min) {
      const playerDuration = playerSettings.min * 60 + playerSettings.sec;
      setDuration(playerDuration);
    } else {
      setDuration(playerSettings.sec);
    }
  }, [gamer.timerKey]);

  const startGame = useCallback(() => {
    dispatch(teamActions.setActivePlayer({ player }));
    dispatch(teamActions.setPlaying({ player }));

    dispatch(
      teamActions.setName({
        player,
        name: gamer.name ? gamer.name : player === 'player1' ? 'Player 1' : 'Player 2',
      }),
    );
  }, [dispatch, gamer.name, player]);

  const passGame = () => {
    if (!activePlayer) return;

    dispatch(teamActions.setPlaying({ player: activePlayer }));

    setTimeout(() => {
      dispatch(teamActions.setPlaying({ player }));
      dispatch(teamActions.setActivePlayer({ player }));
      dispatch(teamActions.toggleUserPlaying({ player }));
    }, 0);

    dispatch(
      teamActions.setName({
        player,
        name: gamer.name || (player === 'player1' ? 'Player 1' : 'Player 2'),
      }),
    );
  };

  return (
    <>
      <Timer
        key={gamer.timerKey}
        isShowPauseButton={params.get('game_mode') !== 'quick game'}
        setPlayerName={setName}
        variant={gamer.teamColor || defaultColor}
        wasPause={
          (!ungamer.isPlaying && !gamer.isPlaying && gamer.remainingTime) || !activePlayer
            ? false
            : gamer.wasPause
        }
        startGame={
          !gamer.isPlaying && inGame && activePlayer && activePlayer !== player
            ? passGame
            : (!ungamer.alreadyPlayed && activePlayer) ||
                (gamer.isPlaying && activePlayer === player)
              ? undefined
              : startGame
        }
        defaultTitle={defaultTitle}
        title={gamer.teamColor ? gamer.name ?? defaultTitle : defaultTitle}
        duration={duration}
        onUpdate={onChangeRemainingTime}
        isPlaying={gamer.isPlaying && activePlayer === player}
        onClickStop={onClickProtest}
        onClickContinue={onClickContinue}
        onClickPause={onClickPause}
        onComplete={onComplete}
        togglePlaying={() => dispatch(teamActions.toggleUserPlaying({ player }))}
        $isActive={!!(gamer.remainingTime && activePlayer === player)}
        $isMainClock={isMainClock}
      />
      <HiddenAudio>
        <audio
          ref={audioRef}
          src="/assets/sounds/gong.mp3"
          preload="auto"
        />
      </HiddenAudio>
    </>
  );
};

export default TimerContainer;
